import { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from "recharts";
import { TrendingUp, TrendingDown, DollarSign, Users, Warehouse, Zap } from "lucide-react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { journalAPI } from "../../services/api";

const PIE_COLORS = ["#3b82f6","#8b5cf6","#10b981","#f59e0b"];
const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Number(n) || 0);

function CurrencyLines({ byCurrency, accent }: { byCurrency: Record<string, number>; accent: string }) {
  const entries = Object.entries(byCurrency || {}).filter(([, v]) => v > 0);
  if (entries.length === 0) return <div style={{ fontSize: 22, fontWeight: 900, color: accent }}>—</div>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {entries.map(([cur, amt]) => (
        <div key={cur} style={{ display: "flex", alignItems: "baseline", gap: 5 }}>
          <span style={{ fontSize: entries.length > 1 ? 16 : 22, fontWeight: 900, color: accent }}>{fmt(amt)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: accent, opacity: 0.75 }}>{cur}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [hoveredCard, setHoveredCard] = useState<number|null>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const res = await journalAPI.dashboardStats({ period: 6 });
      if (res.success) setStats(res.stats);
      setLoading(false);
    };
    load();
  }, []);

  const s = stats || {};

  const cards = [
    { title: "فروش این ماه",          byCurrency: s.sales_by_currency,        up: true,  icon: TrendingUp,   accent: "#10b981", glow: "rgba(16,185,129,0.3)" },
    { title: "خریدهای این ماه",        byCurrency: s.purchases_by_currency,    up: false, icon: DollarSign,   accent: "#3b82f6", glow: "rgba(59,130,246,0.3)" },
    { title: "طلب از مشتریان",         byCurrency: s.receivables_by_currency,  up: true,  icon: Users,        accent: "#8b5cf6", glow: "rgba(139,92,246,0.3)" },
    { title: "مصارف این ماه",          byCurrency: s.expenses_by_currency,     up: false, icon: TrendingDown, accent: "#f59e0b", glow: "rgba(245,158,11,0.3)" },
    { title: "قرضداری به فروشندگان",   byCurrency: s.payables_by_currency,     up: false, icon: Warehouse,    accent: "#ec4899", glow: "rgba(236,72,153,0.3)" },
    { title: "دریافتی این ماه",        byCurrency: s.receipts_by_currency,     up: true,  icon: Zap,          accent: "#06b6d4", glow: "rgba(6,182,212,0.3)" },
  ];

  const jalaliMonths: Record<string, string> = {
    "01": "حمل", "02": "ثور", "03": "جوزا", "04": "سرطان",
    "05": "اسد", "06": "سنبله", "07": "میزان", "08": "عقرب",
    "09": "قوس", "10": "جدی", "11": "دلو", "12": "حوت",
  };

  const toJalaliMonth = (gregorianMonth: string) => {
    // تبدیل تقریبی ماه میلادی به شمسی (هر ماه میلادی ≈ ماه شمسی + 9)
    const m = parseInt(gregorianMonth);
    const j = ((m + 8) % 12) + 1;
    return jalaliMonths[String(j).padStart(2, "0")] || gregorianMonth;
  };

  const chartData = (s.chart_data || []).map((d: any) => ({
    name: d.month ? toJalaliMonth(d.month.slice(5)) : "",
    فروش: Number(d.sales) || 0,
    خرید: Number(d.purchases) || 0,
    مصارف: Number(d.expenses) || 0,
    COGS: Number(d.cogs) || 0,
  }));

  const pieData = [
    { name: "فروش",   value: Number(s.total_sales)     || 0 },
    { name: "خرید",   value: Number(s.total_purchases)  || 0 },
    { name: "مصارف",  value: Number(s.total_expenses)   || 0 },
    { name: "COGS",   value: Number(s.total_cogs)       || 0 },
  ].filter(d => d.value > 0);

  return (
    <div style={{ padding: "28px 28px 40px" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>داشبورد مدیریتی</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text, letterSpacing: "-0.5px" }}>خلاصه عملکرد کسب‌وکار</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>آخرین به‌روزرسانی: همین لحظه</p>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext, fontSize: 14 }}>در حال بارگذاری...</div>
      ) : (
        <>
          {/* کارت‌ها */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 16, marginBottom: 28 }}>
            {cards.map((card, i) => {
              const Icon = card.icon;
              const hovered = hoveredCard === i;
              return (
                <div key={i} onMouseEnter={() => setHoveredCard(i)} onMouseLeave={() => setHoveredCard(null)} style={{
                  borderRadius: 18, padding: "22px 20px",
                  background: dark ? "linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))" : "rgba(255,255,255,0.95)",
                  border: hovered ? `1px solid ${card.accent}55` : `1px solid ${T.cardBorder}`,
                  boxShadow: hovered ? `0 12px 40px ${card.glow}` : dark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 4px 20px rgba(0,0,0,0.06)",
                  transform: hovered ? "translateY(-4px)" : "translateY(0)",
                  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)", cursor: "default", position: "relative", overflow: "hidden"
                }}>
                  <div style={{ position: "absolute", top: -20, left: -20, width: 80, height: 80, borderRadius: "50%", filter: "blur(25px)", background: card.glow, opacity: hovered ? 0.6 : 0.3, transition: "opacity 0.3s" }} />
                  <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 12, background: `${card.accent}18`, border: `1px solid ${card.accent}30`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon size={18} color={card.accent} />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: card.up ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)", color: card.up ? "#10b981" : "#ef4444", border: card.up ? "1px solid rgba(16,185,129,0.2)" : "1px solid rgba(239,68,68,0.2)" }}>
                        {card.up ? "↑" : "↓"}
                      </span>
                    </div>
                    <div style={{ marginBottom: 5 }}><CurrencyLines byCurrency={card.byCurrency} accent={card.accent} /></div>
                    <div style={{ fontSize: 12, color: T.subtext, fontWeight: 500 }}>{card.title}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* نمودارها */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20, marginBottom: 20 }}>
            <div style={{ borderRadius: 18, padding: "22px", background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.95)", border: `1px solid ${T.cardBorder}`, backdropFilter: "blur(12px)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>روند فروش ماهانه</div>
              <div style={{ fontSize: 11, color: T.subtext, marginBottom: 16 }}>بر اساس دالر آمریکا — ۶ ماه اخیر</div>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="areaBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.chartGrid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: T.subtext, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: T.subtext, fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: T.tooltip, border: `1px solid ${T.cardBorder}`, borderRadius: 10, fontSize: 12 }} />
                  <Area type="monotone" dataKey="فروش" stroke="#3b82f6" strokeWidth={2.5} fill="url(#areaBlue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div style={{ borderRadius: 18, padding: "22px", background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.95)", border: `1px solid ${T.cardBorder}`, backdropFilter: "blur(12px)" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>توزیع تراکنش‌ها</div>
              <div style={{ fontSize: 11, color: T.subtext, marginBottom: 16 }}>این ماه</div>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={82} dataKey="value" paddingAngle={3}>
                    {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="none" />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: T.tooltip, border: `1px solid ${T.cardBorder}`, borderRadius: 10, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: T.subtext }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ borderRadius: 18, padding: "22px", background: dark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.95)", border: `1px solid ${T.cardBorder}`, backdropFilter: "blur(12px)" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 4 }}>مقایسه فروش، خرید و مصارف</div>
            <div style={{ fontSize: 11, color: T.subtext, marginBottom: 16 }}>بر اساس دالر آمریکا — ۶ ماه اخیر</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} barGap={4}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.chartGrid} vertical={false} />
                <XAxis dataKey="name" tick={{ fill: T.subtext, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: T.subtext, fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: T.tooltip, border: `1px solid ${T.cardBorder}`, borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="فروش"  fill="#10b981" radius={[6,6,0,0]} />
                <Bar dataKey="خرید"  fill="#3b82f6" radius={[6,6,0,0]} />
                <Bar dataKey="مصارف" fill="#f59e0b" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}